import db from "../db/database";
import { sendToBusinessCentral, fetchFromBusinessCentral, hasError, postInvoice } from "../utils/businessCentralUtils";
import { calculateBCUnitPrice } from "../utils/invoiceTotals";
import { isEmpty } from "../utils/common";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getClientByNoOrId } from "./clientService";
import { syncClients } from "./clientSyncService";
import { getInvoicePaymentLines } from "./invoicePaymentService";
import { getInvoiceLines } from "./invoiceService";
import { getClientById } from './clientService';
import { COUNTRY_CODES } from "../constants/countries"

// Función para transformar las facturas al formato esperado por Business Central
export const transformInvoiceForBC = async (invoice) => {
	try {
		// Obtener el tipo de factura (para poder construir correctamente la estructura)
		const isInvoice = invoice.Document_Type === "Invoice";
		const isCreditMemo = invoice.Document_Type === "Credit Memo";

		const client = !isEmpty(invoice.Sell_to_Customer_No_) ? await db.clients
			.where("No")
			.equals(invoice.Sell_to_Customer_No_)
			.or("id")
			.equals(invoice.Sell_to_Customer_No_)
			.first() : null;

		// Obtener las líneas de pago para calcular el descuento máximo
		const paymentLines = await getInvoicePaymentLines(invoice);

		// Obtener el descuento de la forma de pago
		let paymentDiscountPercentage = 0;

		if (!paymentLines || paymentLines.length === 0) {
			paymentDiscountPercentage = 0;
		}
		else {
			paymentDiscountPercentage = paymentLines.length > 1 ? 0 : paymentLines[0].Dto;
		}

		// Preparar cabecera de la factura
		const customerNo = client?.No || (typeof invoice.Sell_to_Customer_No_ === 'string' ? invoice.Sell_to_Customer_No_ : undefined);
		const header = {
			...invoice,
			Document_Type: invoice.Document_Type,
			Posting_Date: invoice.Posting_Date,
			Document_Date: invoice.Document_Date,
			// Forzar a usar No del cliente; si no existe, dejaremos que la validación superior bloquee
			Sell_to_Customer_No_: customerNo,
			Bill_to_Customer_No_: customerNo,
			// Datos de facturación completos
			Bill_to_Name: invoice.Bill_to_Name,
			Bill_to_Address: invoice.Bill_to_Address,
			Bill_to_City: invoice.Bill_to_City || "",
			VAT_Registration_No_: invoice.VAT_Registration_No_ || "",
			Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || "",
			// Código de dirección de envío
			Ship_to_Code: invoice.Ship_to_Code || "01",
			// Tipo de documento de identificación fiscal
			Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || "Cedula",
			Fiscal_Invoice_Type: invoice.Fiscal_Invoice_Type || "Factura consumidor final",
			NCR: invoice.NCR || "",
			// Datos de envío (usamos los mismos que de facturación)
			Sell_to_Name: invoice.Bill_to_Name,
			Sell_to_Address: invoice.Bill_to_Address,
			Sell_to_City: invoice.Bill_to_City || "",
			Colegio: invoice.Colegio || "",
			Caja: invoice.Caja || invoice.caja || "",
			Turno: invoice.Turno || invoice.turno || "",
			// Incluir campos de descuento
			Dto: paymentDiscountPercentage,
			// Incluir campos de cupón
			Cupon: invoice.Cupon || "",
			Tipo_Detalle: invoice.Tipo_Detalle || "",
			No_Detalle: invoice.No_Detalle || "",
			// Incluir campo de campaña
			Campaign_No_: invoice.Campaign_No_ || "",
			No_Comprobante_Fiscal_Rel: invoice.No_Comprobante_Fiscal_Rel ?? "", // Incluir campos de Nota de creditos
		};
		// Si es un abono, añadir la referencia a la factura original
		if (isCreditMemo) {
			header.Applies_to_Doc_Type = "Invoice";
			header.Applies_to_Doc_No = invoice.Applies_to_Doc_No || "";
			header.EInvDocNoRelated = invoice.Applies_to_Doc_No || "";
		}
		// Crear la estructura completa de datos para enviar a BC
		// El formato depende del tipo de documento
		let data;
		if (isInvoice || isCreditMemo) {
			data = header;
		}
		else {
			throw new Error("Tipo de documento no soportado");
		}

		return data;
	} catch (error) {
		console.error("Error al transformar factura para BC:", error);
		throw error;
	}
};

// Transformar línea de factura para enviarla a Business Central
export const transformInvoiceLineForBC = (invoiceLine, documentNo, index) => {
	// PRECIO SIN IVA - BC lo calcula todo nuevamente
	const unitPriceForBC = calculateBCUnitPrice({
		unitPrice: invoiceLine.Unit_Price,
		ivaPct: invoiceLine.PorIva,
		priceIncludesVAT: invoiceLine.Price_Includes_VAT === true,
	});

	// Crear objeto de línea de factura según formato API BC
	const line = {
		Document_Type: invoiceLine.Document_Type || "Invoice",
		Document_No_: documentNo,
		Line_No_: invoiceLine.Line_No || (index + 1) * 10000,
		Type: invoiceLine.Type || "Item",
		No_: invoiceLine.No || "",
		Description: invoiceLine.Description || "",
		Quantity: invoiceLine.Quantity || 0,
		Unit_Price: unitPriceForBC,
		Line_Discount_: invoiceLine.Line_Discount_ || 0,
		PorIva: invoiceLine.PorIva || 0,
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(line).filter(([_, value]) => value !== "")
	);
};

// Transformar línea de pago para enviarla a Business Central
export const transformInvoicePaymentLineForBC = (
	paymentLine,
	documentNo,
	index
) => {
	// Crear objeto de línea de pago según formato API BC
	const line = {
		Document_Type: paymentLine.Document_Type || "Invoice",
		Document_No_: documentNo,
		Line_No: paymentLine.Line_No || (index + 1) * 10000,
		Payment_Method: paymentLine.Payment_Method || "",
		Amount: paymentLine.Amount || 0,
		Dto: paymentLine.Dto || 0,
		Dto_Amount: paymentLine.Dto_Amount || 0,
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(line).filter(([_, value]) => value !== "")
	);
};

const formatSyncError = (code, details = "") => {
	const messages = {
		CLIENT_NOT_SYNCED: "El cliente no está sincronizado en Business Central.",
		HEADER_CREATION_FAILED: "No se pudo crear la cabecera de la factura en Business Central.",
		LINES_SYNC_FAILED: "No se pudieron enviar las líneas de la factura.",
		NO_LINES: "No se pueden sincronizar facturas sin líneas",
		PAYMENTS_SYNC_FAILED: "No se pudieron enviar las líneas de pago.",
		POSTING_FAILED: "La factura se sincronizó, pero no se pudo contabilizar en Business Central.",
		UNKNOWN_ERROR: "Ocurrió un error inesperado al sincronizar la factura."
	};

	let message = messages[code] || messages.UNKNOWN_ERROR;

	// Agregar detalles opcionales
	if (details) {
		message += ` Detalles: ${details}`;
	}

	return { code, message, details, success: false };
};

// Sincronizar una factura a Business Central
export const syncInvoiceToBC = async (invoice, country) => {
	try {
		// -----------------------------
		// 0. Verificar cliente
		// -----------------------------
		let client = (!isEmpty(invoice.Sell_to_Customer_No_) || !isEmpty(invoice.clientLocalId)) ?
			await getClientByNoOrId(invoice.Sell_to_Customer_No_ || invoice.clientLocalId) :
			null;

		if ((!client || !client.No || client.pendingSync) && !isEmpty(client?.name)) {
			// Intentar refrescar clientes por si se sincronizó recientemente
			await syncClients();
			client = await getClientByNoOrId(invoice.Sell_to_Customer_No_ || invoice.clientLocalId);
			if (!client || !client.No) {
				let error = formatSyncError("CLIENT_NOT_SYNCED");

				return {
					...error,
					pendingClientSync: true,
					clientId: invoice.clientLocalId || invoice.Sell_to_Customer_No_
				};
			}
		}

		// -----------------------------
		// 1. Obtener líneas y pagos
		// -----------------------------
		const invoiceLines = await getInvoiceLines(invoice.id);
		const paymentLines = await getInvoicePaymentLines(invoice);

		//No debe sincronizas si lineas esta vacio
		if (!invoiceLines || !paymentLines || invoiceLines.length == 0 || paymentLines.length == 0) {
			let error = formatSyncError("NO_LINES");
			await db.invoices.update(invoice.id, {
				No: null,
				syncStage: 'error',
				pendingSync: 0,
				lastSynced: getCurrentDateTimeISO(),
			});

			return {
				...error
			};
		}

		// Calcular descuento máximo de formas de pago
		let maxDiscount = 0;
		if (paymentLines && paymentLines.length > 0) {
			maxDiscount = paymentLines.reduce(
				(max, line) => ((line.Dto || 0) > max ? line.Dto || 0 : max),
				0
			);
		}

		// Calcular el total de la factura con IVA específico por producto
		let subtotal = 0;
		let vat = 0;

		invoiceLines.forEach((line) => {
			const quantity = line.Quantity || 0;
			const unitPrice = line.Unit_Price || 0;
			const discount = line.Line_Discount_ || 0;
			const discountAmount = unitPrice * (discount / 100);
			const finalPrice = unitPrice - discountAmount;
			const lineSubtotal = quantity * finalPrice;

			// Aplicar IVA específico del producto
			const lineIva = line.PorIva || 0;
			const lineVat = lineSubtotal * (lineIva / 100);

			subtotal += lineSubtotal;
			vat += lineVat;
		});

		const total = subtotal + vat;
		const importeDescuento = maxDiscount > 0 ? total * (maxDiscount / 100) : 0;

		// Actualizar los campos Dto e importeDto en la factura
		await db.invoices.update(invoice.id, {
			Dto: maxDiscount,
			importeDto: importeDescuento.toFixed(2),
		});

		// Recargar la factura actualizada
		const updatedInvoice = await db.invoices.get(invoice.id);

		// -----------------------------
		// 2. Preparar cabecera para BC
		// -----------------------------
		const invoiceForBC = await transformInvoiceForBC(updatedInvoice);
		const invoiceData = {
			data: JSON.stringify({ Sales_Headers: [invoiceForBC] }),
		};

		// -----------------------------
		// 3. Crear CABECERA si no existe
		// -----------------------------
		let syncStage = updatedInvoice.syncStage || "none";
		let preAssignedDocNo = updatedInvoice.Pre_Assigned_No || "";
		let systemId = updatedInvoice.systemId || "";

		if (!preAssignedDocNo) {
			if (syncStage === "none") {
				await db.invoices.update(invoice.id, {
					syncStage,
				});
			}

			const headerResponse = await sendToBusinessCentral(
				"Importaciones_insertaFacturasVenta",
				invoiceData,
				true
			);

			if (!headerResponse?.value) throw formatSyncError("HEADER_CREATION_FAILED", "BC no devolvió número de documento");

			preAssignedDocNo = headerResponse.value;

			// Si es Paraguay ademas del numero 'Pre_Assigned' del invoice devuelve el 'systemId' para contabilizar la factura inmediatamente
			if (country.code === COUNTRY_CODES.PARAGUAY || country.code === COUNTRY_CODES.HONDURAS) {
				// El ejemplo retorno no es JSON válido: { value: "{\"No.\":\"000033\"\"System Id\":\"{...}\"}" }
				const rawValue = headerResponse.value;
				const fixed = rawValue.replace(/""/g, '","');
				const parsed = JSON.parse(fixed);
				const normalized = {};

				for (const [key, value] of Object.entries(parsed)) {
					const cleanKey = key
						.replace(/[.\s{}]/g, '') // saca puntos, espacios y llaves
						.trim();
					normalized[cleanKey] = value.replace(/[{}]/g, ''); // también limpia el valor si tiene llaves
				}

				preAssignedDocNo = normalized.No;
				systemId = normalized.SystemId;
			}

			// Guardar inmediatamente para evitar duplicados
			syncStage = "header";
			await db.invoices.update(invoice.id, {
				No: null,
				Pre_Assigned_No: preAssignedDocNo,
				systemId: systemId,
				syncStage,
				pendingSync: 1,
				lastSynced: getCurrentDateTimeISO(),
			});

			console.log(`Cabecera creada en BC con Pre_Assigned_No ${preAssignedDocNo}`);
		}

		// -----------------------------
		// 4. Enviar LÍNEAS si no se hizo
		// -----------------------------
		if (syncStage === "none" || syncStage === "header") {
			const transformedLines = invoiceLines.map((line, index) =>
				transformInvoiceLineForBC(line, preAssignedDocNo, index)
			);
			const linesData = {
				data: JSON.stringify({ Sales_Lines: transformedLines }),
			};
			console.log(`Enviando líneas de factura`);
			const responseLineas = await sendToBusinessCentral(
				"Importaciones_insertaLineasFacturasVenta",
				linesData,
				true
			);

			if (responseLineas?.data && hasError(responseLineas?.data)) {
				throw formatSyncError("LINES_SYNC_FAILED");
			}

			syncStage = "lines";
			await db.invoices.update(invoice.id, {
				syncStage,
				lastSynced: getCurrentDateTimeISO(),
			});

			for (const line of invoiceLines) {
				await db.invoiceLines.update(line.id, {
					lastSynced: getCurrentDateTimeISO(),
					pendingSync: 0,
				});
			}
			console.log("Líneas enviadas correctamente");
		}

		// -----------------------------
		// 5. Enviar PAGOS si no se hizo
		// -----------------------------
		if ((paymentLines?.length ?? 0) > 0) {
			if (syncStage === "none" || syncStage === "header" || syncStage === "lines") {
				const transformedPaymentLines = paymentLines.map((line, index) =>
					transformInvoicePaymentLineForBC(line, preAssignedDocNo, index)
				);
				const paymentLinesData = {
					data: JSON.stringify({ Sales_Payment_Lines: transformedPaymentLines }),
				};

				console.log(`Enviando líneas de pago…`);
				const responseLineasPago = await sendToBusinessCentral(
					"Importaciones_insertaPagosFacturasVenta",
					paymentLinesData,
					true
				);

				if (responseLineasPago?.data && hasError(responseLineasPago?.data)) {
					throw formatSyncError("PAYMENTS_SYNC_FAILED");
				}

				syncStage = "payments";
				await db.invoices.update(invoice.id, {
					syncStage,
					pendingSync: 0,
					lastSynced: getCurrentDateTimeISO(),
				});
				console.log("Pagos enviados correctamente");
			}

			// Actualizar estado de sincronización de líneas de pago
			for (const line of paymentLines) {
				await db.invoicePaymentLines.update(line.id, {
					Document_No_: preAssignedDocNo,
					lastSynced: getCurrentDateTimeISO(),
					pendingSync: 0,
				});
			}
		}

		// -----------------------------
		// 6. Contabilizar si es Paraguay|Honduras
		// -----------------------------
		if (country.code === COUNTRY_CODES.PARAGUAY || country.code === COUNTRY_CODES.HONDURAS) {
			if (!systemId) console.warn("Factura sin systemId, no se contabiliza");
			else {
				try {
					console.log("Contabilizando…");
					const isCreditMemo = invoice.Document_Type === 'Credit Memo';

					const isPosted = await postInvoice(systemId, isCreditMemo);

					if (isPosted) {
						//Chequeamos si BC dio error pero contabilizo igualmente
						return await recuperarFacturaContabilizada(invoice, country);
					}
					else {
						throw ("No fue posible contabilizar la factura.");
					}
				}
				catch (err) {
					console.warn("Error al contabilizar, pero la factura está sincronizada en BC", err);
					try {
						//Chequeamos si BC dio error pero contabilizo igualmente
						return await recuperarFacturaContabilizada(invoice, country);
					} catch (recoveryError) {
						console.warn("No se pudo recuperar consecutivo final tras error de contabilización", recoveryError);

						// La consideramos sincronizada, pero no contabilizada
						await db.invoices.update(invoice.id, {
							posted: 0,
							syncStage: "payments",
							pendingSync: 0,
							lastSynced: getCurrentDateTimeISO(),
						});

						return {
							success: true,
							message: "Factura sincronizada, pero no se pudo contabilizar automáticamente. Reintente desde BC.",
						};
					}
				}
			}
		}

		// -----------------------------
		// 7. Finalizado (países ≠ Paraguay|Honduras)
		// -----------------------------
		await db.invoices.update(invoice.id, {
			pendingSync: 0,
			syncStage: "posted",
			lastSynced: getCurrentDateTimeISO(),
		});
		return { success: true, message: "Factura sincronizada correctamente" };
	} catch (error) {
		if (error?.code) {
			return { ...error };
		}
		return { ...formatSyncError("UNKNOWN_ERROR", error.message) };
	}
};

// Sincronizar todas las facturas pendientes
export const syncInvoices = async (country) => {
	try {
		// Obtener facturas pendientes de sincronización que NO sean borradores
		const pendingInvoices = await db.invoices
			.where("pendingSync")
			.equals(1)
			.and((invoice) => !invoice.isDraft && !invoice.deleted) // Excluir borradores y facturas eliminadas
			.toArray();

		const results = [];
		const pendingClientSyncInvoices = [];

		// Sincronizar cada factura pendiente
		for (const invoice of pendingInvoices) {
			const result = await syncInvoiceToBC(invoice, country);

			if (result.pendingClientSync) {
				// La factura tiene un cliente que no está sincronizado
				pendingClientSyncInvoices.push({
					invoiceId: invoice.id,
					clientId: result.clientId,
					message: result.message,
				});
				console.log(
					`Factura ${invoice.id} pendiente de sincronización del cliente ${result.clientId}`
				);
			} else {
				results.push(result);
			}
		}

		// Si hay facturas pendientes de sincronización de clientes, mostrar en consola
		if (pendingClientSyncInvoices.length > 0) {
			console.log(
				"Las siguientes facturas están pendientes de sincronización de clientes:",
				pendingClientSyncInvoices
			);
		}

		return {
			success: true,
			results,
			pendingClientSync: pendingClientSyncInvoices.length > 0,
			pendingClientSyncInvoices,
		};
	} catch (error) {
		console.error("Error al sincronizar facturas:", error);
		return { success: false, message: error.message };
	}
};

export async function updateDocumentNumberReferences(oldNo, newNo, invoiceId) {
	// 1. Actualizar invoiceLines
	await db.invoiceLines
		.where("invoiceId")
		.equals(invoiceId)
		.modify({ invoiceNo: newNo });

	// 2. Actualizar invoicePaymentLines
	await db.invoicePaymentLines
		.where("invoiceId")
		.equals(invoiceId)
		.modify({ Document_No_: newNo });

	// 3. Actualizar formaDePago
	await db.formaDePago
		.where("DocumentNo")
		.equals(oldNo)
		.modify({ DocumentNo: newNo });
}

export async function getFinalDocumentNo(invoice, country) {
	/**
	* Determinar si el documento ya tiene número definitivo.
	* Pre_Assigned_No vacío significa:
	*   → el campo "No" es el número pre-asignado
	*   → aún NO se ha recuperado el número definitivo
		→ es Document_Type === 'Invoice'. Las 'Credit Memo' siempre cuentan con Pre_Assigned_No de la factura relacionada
	*/
	const isCreditMemo = invoice.Document_Type === 'Credit Memo';
	const hasDefinitiveNumber = invoice.No;

	// Si ya tiene número definitivo, serie y systemId, devolvemos el existente y salimos.
	if (hasDefinitiveNumber && (invoice.No_Serie_NCF_Facturas || invoice.No_Serie_NCF_Abonos) && invoice.systemId) {
		return invoice.No;
	}

	let cliente = invoice.Customer_No ?? invoice.Sell_to_Customer_No_;

	try {
		if (typeof cliente === "number" && !isNaN(cliente)) {
			const clientData = await getClientById(cliente);
			cliente = clientData?.No ?? null;
		}
	}
	catch (error) {
		cliente = null;
	}

	const filters = [
		cliente && `Sell_to_Customer_No eq '${cliente}'`,
		`Pre_Assigned_No eq '${invoice.Pre_Assigned_No}'`,
		`TPV eq '${invoice.caja}'`,
		`Posting_Date eq ${invoice.Posting_Date}`
	].filter(Boolean); // elimina vacíos

	const filter = filters.join(" and ");

	let endpoint = isCreditMemo ? `PostedSalesCreditMemo` : `PostedSalesInvoice`;
	let isApi = false;

	//Honduras|Salvador tiene su propia api para PostedSalesInvoices porque no funciona OData en BC
	if (country.code === COUNTRY_CODES.HONDURAS || country.code === COUNTRY_CODES.ELSALVADOR) {
		isApi = !isCreditMemo;
		endpoint = isCreditMemo ? `PostedSalesCreditMemo` : "postedSalesInvoicesSant";
	}

	const results = await fetchFromBusinessCentral(endpoint, filter, isApi);

	if (!Array.isArray(results) || results.length === 0) {
		throw new Error(
			`No fue posible recuperar el número de factura con Pre_Assigned_No '${invoice.Pre_Assigned_No}'.`
		);
	}

	const finalDocumentNo = results[0].No;

	if (!finalDocumentNo) {
		return null;
	}

	await db.invoices.update(invoice.id, {
		Pre_Assigned_No: invoice.Pre_Assigned_No,
		No: finalDocumentNo,
		No_Serie_NCF_Facturas: results[0]?.No_Serie_NCF_Facturas || "",
		No_Serie_NCF_Abonos: results[0]?.No_Serie_NCF_Abonos || "",
		No_Comprobante_Fiscal: results[0]?.No_Comprobante_Fiscal,
		Posting_Date: results[0]?.Posting_Date,
		systemId: results[0]?.systemId || results[0]?.SystemId || "",
		posted: 1,
		syncStage: "posted",
		pendingSync: 0,
		lastSynced: getCurrentDateTimeISO(),
	});

	await updateDocumentNumberReferences(invoice.Pre_Assigned_No, finalDocumentNo, invoice.id);

	return finalDocumentNo;
}

export async function recuperarFacturaContabilizada(invoice, country) {
	const latestInvoice = await db.invoices.get(invoice.id);
	//Si tiene NoFinal actualiza estado invoice dentro de getFinalDocumentNo
	const finalNo = await getFinalDocumentNo(latestInvoice, country);

	//Si no recupera NoFinal significa que no contabilizo, guardamos en step anterior
	if (!finalNo) {
		await db.invoices.update(invoice.id, {
			posted: 0,
			syncStage: "payment",
			pendingSync: 0,
			lastSynced: getCurrentDateTimeISO(),
		});
	}

	return {
		success: true,
		message: finalNo ? "Factura contabilizada correctamente." : "Factura sincronizada, pero no se pudo contabilizar automáticamente. Reintente desde BC.",
	};
}


